<script setup lang="ts">
import { useClaims } from '@auth/useClaims';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import {
  getStandaardAlertsGetByAdviseurQueryKey,
  standaardAlertsGetByAdviseur,
} from '@services/alerts-public/orval-api/standaard-alerts/standaard-alerts';
import { type CrmAdviseursGetAdviseursDto as TussenPersonenDto } from '@services/crm-internal/api';
import { VButton } from '@shared/component/VButton';
import VSidePanel from '@shared/component/VSidePanel/VSidePanel.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { mapErrors } from '@shared/validation/vuelidate';
import { useQuery } from '@tanstack/vue-query';
import { useInsertAtCursorPosition } from '@tools/insertAt';
import { useConfirmDialog } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import MultiCheckboxField from '@shared/form/CheckboxField/MultiCheckboxField.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import AlertVariabelenButtons from '@alert-lib/AlertVariabelenButtons.vue';
import { frequentieOptions, ontvangerOptions } from '@alert-lib/alertOptions';
import { useStandaardAlertStore } from './standaardalert.store';

const props = defineProps<{
  standaardalertId?: string;
  geselecteerdeAdviseur?: TussenPersonenDto;
}>();
const emit = defineEmits<{ formClosed: [boolean] }>();
const notification = useNotification();
const { isRevealed, reveal, confirm, cancel } = useConfirmDialog();
const claims = useClaims();

const standaardAlertStore = useStandaardAlertStore();
const adviseurNaam = computed(() => props.geselecteerdeAdviseur?.name || '');

const queryKey = computed(() =>
  getStandaardAlertsGetByAdviseurQueryKey({
    adviseurnummer: props.geselecteerdeAdviseur?.id,
  }),
);

const textAreaRef = ref(null);
const { insertAtCursorPosition } = useInsertAtCursorPosition(textAreaRef);

const { data: standaardAlerts, error: standaardAlertsError } = useQuery({
  queryKey: [queryKey],
  queryFn: () => standaardAlertsGetByAdviseur({ adviseurnummer: props.geselecteerdeAdviseur?.id }),
  select: (result) => result?.result ?? [],
});

useWatchErrorHandler(standaardAlertsError, {
  onError(error) {
    notification.error(error.message ?? 'Standaard alerts konden niet worden geladen');
  },
});
const { isSavingAlerts, validator, standaardAlert } = storeToRefs(standaardAlertStore);

onMounted(async () => {
  standaardAlertStore.successCb = confirm;
  if (props.standaardalertId) {
    const foundStandaardAlert = standaardAlerts.value?.find((a) => a.id === props.standaardalertId);
    if (!foundStandaardAlert) return notification.error('Standaard alert niet gevonden');

    standaardAlert.value = {
      ...foundStandaardAlert,
    };
  } else {
    standaardAlertStore.reset();
  }
  await reveal();

  emit('formClosed', true);
});
</script>

<template>
  <VSidePanel :open="isRevealed" @close="cancel" :width="800">
    <template #header>
      <VHeading class="text-white" :level="3">
        Standaard alert {{ props.standaardalertId ? 'wijzigen' : 'aanmaken' }}
      </VHeading>
    </template>
    <template #default>
      <FieldRowGroup>
        <FieldRowV2 v-if="claims?.isInternal" label="Adviseur" :label-size="1">
          <TextField :rows="1" :readonly="true" v-model:value="adviseurNaam" />
        </FieldRowV2>
        <FieldRowV2 label="Titel" :label-size="1" :errors="mapErrors(validator.titel)">
          <TextField
            :rows="1"
            placeholder="Vul hier je gewenst titel tekst in"
            :max-length="40"
            v-model:value="standaardAlert.titel"
            focus-on-mount
          />
        </FieldRowV2>
        <AlertVariabelenButtons @selected="insertAtCursorPosition" />
        <FieldRowV2 label="Tekst" :label-size="1" :errors="mapErrors(validator.inhoud)">
          <TextField
            ref="textAreaRef"
            :rows="6"
            placeholder="Vul hier je gewenst alert tekst in"
            :max-length="4000"
            v-model:value="standaardAlert.inhoud"
          />
        </FieldRowV2>
        <FieldRowV2 label="Frequentie" :label-size="1" :errors="mapErrors(validator.frequentie)">
          <SelectField :options="frequentieOptions" v-model:value="standaardAlert.frequentie" />
        </FieldRowV2>
        <FieldRowV2 label="Naar" :label-size="1" :errors="mapErrors(validator.ontvangers)">
          <MultiCheckboxField
            path="ontvangers"
            :options="ontvangerOptions!"
            v-model="standaardAlert.ontvangers"
          />
        </FieldRowV2>
      </FieldRowGroup>
    </template>
    <template #footer>
      <VButton
        prepend-icon="fas fa-save"
        @click="standaardAlertStore.saveStandaardAlert(props.geselecteerdeAdviseur?.id)"
        :loading="isSavingAlerts"
      >
        Opslaan
      </VButton>
    </template>
  </VSidePanel>
</template>
